import { ReportType } from '@/app/constants/reportType'

/**
 * Domyslne parametry wydruku - odpowiadaja poczatkowemu stanowi formularzy
 * renderowanych przez PrintoutParamsRenderer. Uzywane przy generowaniu wielu
 * raportow naraz, kiedy nie otwieramy okna parametrow dla kazdego z nich.
 */
const BASE_PARAMS = {
  separateFiles: false,
  issuerSignature: false,
  driverSignature: false,
}

export const withPrintoutData = (
  report: ReportType,
  data: Record<string, unknown> = {},
): Record<string, unknown> => {
  if (report === ReportType.SPECIFIC_BUSINESS_TRIP_COMMAND) {
    return { ...data, showAmounts: false }
  }
  return data
}

export const getDefaultReportParams = (
  report: ReportType,
): Record<string, any> => {
  switch (report) {
    // Violations.tsx
    case ReportType.VIOLATIONS:
      return {
        ...BASE_PARAMS,
        printDayActivities: true,
        printDayNotes: true,
        issuerSignature: true,
        driverSignature: true,
      }

    // ViolationsGrouped.tsx
    case ReportType.GROUPED_VIOLATIONS:
      return {
        ...BASE_PARAMS,
        showDetails: false,
        driverCharge: false,
      }

    // AvailableTime.tsx
    case ReportType.DRIVER_AVAILABLE_TIME:
      return {
        ...BASE_PARAMS,
        availableTimeFromNow: true,
      }

    // ComplexRaportForm.tsx
    case ReportType.DRIVER_ACTIVITIES:
    case ReportType.VEHICLE_ACTIVITIES:
      return {
        ...BASE_PARAMS,
        availability: true,
        driving: true,
        work: true,
        rest: true,
        noData: true,
        crew: false,
        outOfScope: false,
        manualEntry: false,
        ferryTrainCrossing: false,
        crewEnabled: false,
        outOfScopeEnabled: false,
        manualEntryEnabled: false,
        ferryTrainCrossingEnabled: false,
        activityDurationMinTime: 5,
        activityDurationMinTimeEnabled: false,
        activityBetweenHoursEnabled: false,
        activityBetweenHoursStartTime: '00:00',
        activityBetweenHoursEndTime: '23:59',
      }

    // Delegations.tsx - bez tripReportOption serwer bierze filtr zapamiętany
    // dla raportu, jak WPF.
    case ReportType.TRIP_SUMMARY:
    case ReportType.BASIC_TRIP_PRINT:
    case ReportType.GROUPED_TRIP_BASICS:
    case ReportType.TRIP_GENERAL_STATEMENT:
    case ReportType.TRIP_DETAILS:
    case ReportType.BUSINESS_TRIP_COMMAND:
    case ReportType.TRIP_INVOICES:
    case ReportType.FOREIGN_ALLOWANCES:
      return {
        ...BASE_PARAMS,
        onlySettled: true,
        onlyPolish: true,
      }

    case ReportType.SPECIFIC_BUSINESS_TRIP_COMMAND:
      return withPrintoutData(report, {
        ...BASE_PARAMS,
        tripReportOption: 0,
        onlySettled: true,
        onlyPolish: true,
      })

    case ReportType.MANAGER_REPORT_2:
    case ReportType.TIMESHEET_DETAILED_WAGE_REPORT:
      return {
        ...BASE_PARAMS,
        tripReportOption: 0,
        onlySettled: true,
        onlyPolish: true,
      }

    case ReportType.TRIP_COSTS:
      return {
        ...BASE_PARAMS,
        onlySettled: true,
      }

    // Timesheet.tsx - pole `amounts` swiadomie pomijamy, o wydruku wyliczen
    // decyduje wtedy konfiguracja firmy po stronie serwera.
    case ReportType.TIMESHEET:
    case ReportType.TIMESHEET_APPLICATION_FOR_OVERHOURS_FOR_DRIVERS:
      return {
        ...BASE_PARAMS,
        timesheetOption: 0,
        period: true,
        printDayNotes: false,
        printDayActivities: false,
      }

    case ReportType.TIMESHEET_APPLICATION_FOR_LEAVE_FOR_DRIVERS:
      return {
        ...BASE_PARAMS,
        period: true,
      }

    // ManagerReport.tsx
    case ReportType.MANAGER_REPORT_3:
      return {
        ...BASE_PARAMS,
        manager3ReportOption: 0,
      }

    // TimesheetHours.tsx
    case ReportType.TIMESHEET_SUMMARY:
      return {
        ...BASE_PARAMS,
        groupByDate: true,
      }

    // EuRate.tsx
    case ReportType.TIMESHEET_FOREIGN_AMOUNTS_REPORT:
      return {
        ...BASE_PARAMS,
        timesheetOption: 0,
        printDayNotes: false,
        printDayActivities: false,
        countries: [],
      }

    // LumpSumSettlement.tsx
    case ReportType.TIMESHEET_LUMPSUM_SETTLEMENT:
    case ReportType.TIMESHEET_LUMPSUM_SETTLEMENT_SUMMARY:
      return {
        ...BASE_PARAMS,
        onlyForActiveVehicles: false,
        dontShowCalculatedAmounts: false,
        printTimesheetAmounts: true,
        showCoveredAmounts: false,
        groupByDate: true,
        allowDutyLumpSumFromRateDeduction: false,
        groupBySettlementPeriods: false,
      }

    // TimesheetWorkingHours.tsx
    case ReportType.TIMESHEET_WORKING_HOURS:
      return {
        ...BASE_PARAMS,
        showDetailed: false,
        companyId: '',
      }

    // StatementOnEmployeesCount.tsx - domyslnie poprzedni rok
    case ReportType.STATEMENT_ON_EMPLOYEES_COUNT:
      return {
        ...BASE_PARAMS,
        selectedYear: new Date().getFullYear() - 1,
        leaveEmpty: false,
        averageBasedOnContracts: false,
      }

    // ShortRaportForm.tsx (m.in. DRIVER_DAY_DETAILS, TIMESHEET_TIME_SUMMARY,
    // DRIVER_INSTRUCTION, OUTGOING_CHECKLIST, DRIVER_CARD_APPLICATION)
    default:
      return { ...BASE_PARAMS }
  }
}
